import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, UserCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/auth";
import { Button, Card, Spinner } from "@/components/ui";
import { useOnboardingDraft } from "./useOnboardingDraft";
import type { OnboardingContext, StepProps } from "./types";
import Step1Identity from "./steps/Step1_Identity";
import Step2Employment from "./steps/Step2_Employment";
import Step3NextOfKin from "./steps/Step3_NextOfKin";
import Step4EmergencyMedical from "./steps/Step4_EmergencyMedical";
import Step5Payroll from "./steps/Step5_Payroll";
import Step6Review from "./steps/Step6_Review";

const STEP_TITLES = [
  "Personal details",
  "Contact & address",
  "Employment",
  "Next of kin & emergency",
  "Payroll & superannuation",
  "Review & submit",
];

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const { targetUserId } = useParams<{ targetUserId?: string }>();
  const selfUserId = useAuthStore(s => s.user?.user_id ?? null);

  const { data: context, isLoading, error } = useQuery({
    queryKey: ["onboarding_context", targetUserId ?? "self"],
    queryFn: async () => {
      const { data, error } = await supabase.schema("app").rpc("get_my_onboarding_context", {
        p_target_user_id: targetUserId ?? undefined,
      });
      if (error) throw error;
      return data as unknown as OnboardingContext;
    },
  });

  const draftKey = context?.user?.user_id ?? selfUserId;
  const {
    draft, currentStep: step, setCurrentStep: setStep,
    updateField, updateMany, clear,
  } = useOnboardingDraft(draftKey);

  const skipPayroll = !(context?.is_director ?? false);

  const next = () => {
    if (step === 4 && skipPayroll) setStep(6);
    else if (step < 6) setStep(step + 1);
  };
  const back = () => {
    if (step === 6 && skipPayroll) setStep(4);
    else if (step > 1) setStep(step - 1);
  };
  const saveAndExit = () => navigate("/");

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }
  if (error || !context) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="text-danger text-sm">
          Failed to load onboarding context. Try reloading.
        </div>
      </div>
    );
  }

  const progressPct = (step / 6) * 100;
  const stepProps: StepProps = {
    context, draft, updateField, updateMany,
    setCurrentStep: setStep,
    clearDraft: clear,
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {!context.is_self && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent/10 border border-accent/30 flex items-center gap-3">
          <UserCircle size={16} className="text-accent" />
          <div className="text-sm">
            <span className="text-text-muted">Completing onboarding for</span>{" "}
            <span className="font-medium">
              {context.employee
                ? `${context.employee.first_name} ${context.employee.last_name}`
                : context.auth_email ?? "—"}
            </span>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-text-muted text-[11px] uppercase tracking-wider2">
            Step {step} of 6
          </div>
          <Button variant="ghost" size="sm" onClick={saveAndExit}>
            Save & exit
          </Button>
        </div>
        <div className="h-1 bg-line rounded-full overflow-hidden mb-3">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <h1 className="text-xl font-semibold text-text">
          {STEP_TITLES[step - 1]}
        </h1>
      </div>

      <Card className="mb-6">
        <div className="p-6">
          {step === 1 && <Step1Identity {...stepProps} />}
          {step === 2 && <Step2Employment {...stepProps} />}
          {step === 3 && <Step3NextOfKin {...stepProps} />}
          {step === 4 && <Step4EmergencyMedical {...stepProps} />}
          {step === 5 && !skipPayroll && <Step5Payroll {...stepProps} />}
          {step === 6 && <Step6Review {...stepProps} />}
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          icon={ChevronLeft}
          onClick={back}
          disabled={step === 1}
        >
          Back
        </Button>
        {step < 6 && (
          <Button variant="primary" onClick={next}>
            Next <ChevronRight size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}
